import { QueryClient } from "@tanstack/react-query";
import {
  json,
  LoaderFunctionArgs,
  redirect,
  useLoaderData,
} from "react-router-dom";

import { orgStorage } from "@/shared/auth";
import { SearchParams } from "@/shared/environment/SearchParams";
import { RobotoUnauthorizedException } from "@/shared/http";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { Goto } from "@/shared/navigation/Goto";
import { layoutNavigationState } from "@/shared/navigation/layoutNavigation";
import { ErrorMonitoringService } from "@/shared/services";
import { Datasource } from "@/shared/state/visualization";

import {
  EventResolution,
  eventDatasources,
  eventIdOf,
  eventNotices,
  eventSidebarFor,
  resolveEvent,
  resolveEventPanels,
} from "./eventContribution";
import { UnrecoverableError } from "./exceptions";
import {
  resolveByDefaultLayout,
  resolveByLayoutFromNavigationState,
  resolveByLayoutName,
  resolveByWorkspaceId,
} from "./resolvers";
import {
  ConfigSource,
  VisualizerInitContext,
  VisualizerInitData,
  VisualizerNotice,
} from "./visualizerInit";

/**
 * react-router loader for the /visualize route. Resolves the workspace's
 * config source (a saved workspace, a layout, or nothing), the datasources to
 * load, the sidebar view the linked event opens, and any banners to raise.
 *
 * @link https://reactrouter.com/6.30.3/route/loader
 */
export const loader =
  (queryClient: QueryClient) =>
  async ({ request, params }: LoaderFunctionArgs): Promise<Response> => {
    const url = new URL(request.url);

    const httpClient = await makeAuthenticatedHttpClient();
    const orgRecord = orgStorage.getCurrentOrg();

    // Redirect user to select orgs if org not found
    if (!orgRecord) {
      return redirect(`${Goto.route.SignIn}?chooseorg=true`);
    }

    const ctx: VisualizerInitContext = {
      eventId: url.searchParams.get(SearchParams.EVENT) ?? undefined,
      httpClient,
      layoutFromNavState: layoutNavigationState.get(),
      layoutName: url.searchParams.get(SearchParams.LAYOUT) ?? undefined,
      orgId: orgRecord.org_id,
      queryClient,
      workspaceId: params.workspaceId,
    };

    try {
      // Fetch the event once. Its datasources, panels, sidebar view, and
      // notices all read from this single resolution.
      const eventResolution = await resolveEventSafely(ctx);

      const configSource = await resolveConfigSource(ctx, eventResolution);

      const urlDatasources = readUrlDatasources(url.searchParams);

      return json(
        assembleInitData(ctx, configSource, eventResolution, urlDatasources),
      );
    } catch (error) {
      if (error instanceof UnrecoverableError) {
        // Let the error boundary handle this error
        throw error;
      }

      if (error instanceof RobotoUnauthorizedException) {
        // User is authenticated but lacks permission to view this resource
        return redirect(`${Goto.route.Unauthorized}`);
      }

      throw error;
    }
  };

/**
 * Resolve the event named in the URL, treating an unexpected fetch failure as
 * "no event": a transient error loading the linked event must not stop the
 * workspace from opening.
 */
async function resolveEventSafely(
  ctx: VisualizerInitContext,
): Promise<EventResolution | null> {
  try {
    return await resolveEvent(ctx);
  } catch (error) {
    ErrorMonitoringService.captureError(error);
    return null;
  }
}

/**
 * Choose the config source from the first resolver that yields one, in
 * priority order: a shared workspace, a named layout, a handed-off layout,
 * the event's own panels, then the user's default layout.
 *
 * @throws UnrecoverableError when a workspace is missing or its config is invalid
 * @throws RobotoUnauthorizedException when the user may not view the resolved resource
 */
async function resolveConfigSource(
  ctx: VisualizerInitContext,
  eventResolution: EventResolution | null,
): Promise<ConfigSource> {
  const resolvers = [
    () => resolveByWorkspaceId(ctx),
    () => resolveByLayoutName(ctx),
    () => resolveByLayoutFromNavigationState(ctx),
    () => Promise.resolve(resolveEventPanels(eventResolution)),
    () => resolveByDefaultLayout(ctx),
  ];

  for (const resolve of resolvers) {
    try {
      const result = await resolve();
      if (result !== null) {
        return result;
      }
    } catch (error) {
      if (
        error instanceof UnrecoverableError ||
        error instanceof RobotoUnauthorizedException
      ) {
        throw error;
      }

      // Log unexpected errors and fall through to the next resolver.
      ErrorMonitoringService.captureError(error);
    }
  }

  return null;
}

/**
 * Assemble the loader's result from the resolved config source and event.
 * A restored workspace carries its own datasources and drops the event; every
 * other source loads the URL's and the event's datasources together.
 */
function assembleInitData(
  ctx: VisualizerInitContext,
  configSource: ConfigSource,
  eventResolution: EventResolution | null,
  urlDatasources: Datasource[],
): VisualizerInitData {
  if (configSource?.type === "workspace") {
    return {
      configSource,
      // The restored workspace carries its own datasources.
      datasources: [],
      eventSidebar: eventResolution
        ? { eventId: eventIdOf(eventResolution), view: "none" }
        : undefined,
      notices: eventResolution
        ? [
            eventDroppedForWorkspaceNotice(
              ctx.workspaceId,
              eventIdOf(eventResolution),
            ),
          ]
        : [],
    };
  }

  return {
    configSource,
    datasources: mergeDedupDatasources(
      urlDatasources,
      eventDatasources(eventResolution),
    ),
    eventSidebar: eventSidebarFor(eventResolution),
    notices: eventNotices(eventResolution),
  };
}

/**
 * The banner shown when a shared workspace opens with an event in the URL:
 * the workspace restores its own data, so the event is not applied. Names the
 * workspace when its id is known.
 */
function eventDroppedForWorkspaceNotice(
  workspaceId: string | undefined,
  eventId: string,
): VisualizerNotice {
  const workspace = workspaceId
    ? `workspace '${workspaceId}'`
    : "a saved workspace";
  return {
    name: "EventDroppedForWorkspaceNotice",
    title: "Event not loaded",
    message: `The URL includes both ${workspace} and event '${eventId}' references, but they are mutually exclusive. The workspace opened with its saved panels and data.`,
    severity: "info",
  };
}

/** Read the datasources named directly in the URL, collapsing duplicates. */
function readUrlDatasources(searchParams: URLSearchParams): Datasource[] {
  const datasetIds = [...new Set(searchParams.getAll(SearchParams.DATASET))];
  const fileIds = [...new Set(searchParams.getAll(SearchParams.FILE))];

  return [
    ...datasetIds.map((datasetId) => ({
      type: "dataset" as const,
      value: { datasetId },
    })),
    ...fileIds.map((fileId) => ({
      type: "file" as const,
      value: { fileId },
    })),
  ];
}

/** Merge two datasource lists, keeping the first occurrence of each. */
function mergeDedupDatasources(
  first: Datasource[],
  second: Datasource[],
): Datasource[] {
  const seen = new Set<string>();
  const merged: Datasource[] = [];

  for (const datasource of [...first, ...second]) {
    const key =
      datasource.type === "dataset"
        ? `dataset:${datasource.value.datasetId}`
        : `file:${datasource.value.fileId}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    merged.push(datasource);
  }

  return merged;
}

/**
 * react-router shouldRevalidate for the /visualize route.
 *
 * @link https://reactrouter.com/6.30.3/route/should-revalidate
 */
export function shouldRevalidate() {
  // The loader reads the URL (`?event=`, `?dataset=`, `?file=`, `?layout=`)
  // only to seed the initial workspace state. URL changes after that
  // (e.g. adding files to the workspace rewrites `?dataset=`/`?file=`) are
  // read reactively by the workspace, so revalidation is never needed.
  return false;
}

/**
 * Retrieve the loader data for the /visualize route.
 */
export function useWorkspaceLoaderData(): VisualizerInitData {
  return useLoaderData() as VisualizerInitData;
}
